/**
 * Catalog view 컨테이너 — Kinds / Domains / Relations / Shapes / Orphans 를 세로로 나열.
 *
 * Diagram view 의 대안 뷰. 편집 액션은 훅을 통해 상위 에디터(OntologyEditor)로 전달된다.
 */
import type { OntologySlice, OntologyUsage } from "../../api/admin-client.ts"
import { DomainCardGrid } from "./DomainCardGrid.tsx"
import { KindCardGrid } from "./KindCardGrid.tsx"
import type { MetaSelection } from "./MetaEditPanel.tsx"
import { OrphanInbox } from "./OrphanInbox.tsx"
import { RelationCardGrid } from "./RelationCardGrid.tsx"
import { ShapeMatrix } from "./ShapeMatrix.tsx"
import type { OntologyDraft } from "./useOntologyDraft.ts"

interface OntologyCatalogProps {
	readonly draft: OntologySlice
	readonly usage: OntologyUsage | undefined
	readonly selection: MetaSelection
	readonly actions: OntologyDraft
	readonly onSelect: (selection: MetaSelection) => void
	readonly onAddKind: () => void
	readonly onAddDomain: () => void
	readonly onAddRelation: () => void
	readonly onAbsorbOrphan: (type: "kinds" | "domains" | "relations", name: string) => void
	readonly onAbsorbAll: () => void
}

export function OntologyCatalog(props: OntologyCatalogProps) {
	const selectedKindName = props.selection?.type === "kind" ? props.selection.value.name : undefined
	const selectedDomainName = props.selection?.type === "domain" ? props.selection.value.name : undefined
	const selectedRelationName = props.selection?.type === "relation" ? props.selection.value.name : undefined

	return (
		<div className="ontology-catalog">
			<KindCardGrid
				kinds={props.draft.kinds}
				{...(props.usage ? { usage: props.usage.kinds } : {})}
				{...(selectedKindName !== undefined ? { selectedName: selectedKindName } : {})}
				onSelect={(name) => {
					const kind = props.draft.kinds.find((k) => k.name === name)
					if (kind) props.onSelect({ type: "kind", value: kind })
				}}
				onAdd={props.onAddKind}
				onDelete={props.actions.removeKind}
			/>
			<DomainCardGrid
				domains={props.draft.domains}
				{...(props.usage ? { usage: props.usage.domains } : {})}
				{...(selectedDomainName !== undefined ? { selectedName: selectedDomainName } : {})}
				onSelect={(name) => {
					const domain = props.draft.domains.find((d) => d.name === name)
					if (domain) props.onSelect({ type: "domain", value: domain })
				}}
				onAdd={props.onAddDomain}
				onDelete={props.actions.removeDomain}
			/>
			<RelationCardGrid
				relations={props.draft.relations}
				{...(props.usage ? { usage: props.usage.relations } : {})}
				{...(selectedRelationName !== undefined ? { selectedName: selectedRelationName } : {})}
				onSelect={(name) => {
					const rel = props.draft.relations.find((r) => r.name === name)
					if (rel) props.onSelect({ type: "relation", value: rel })
				}}
				onAdd={props.onAddRelation}
				onDelete={props.actions.removeRelation}
			/>
			<ShapeMatrix
				kinds={props.draft.kinds}
				relations={props.draft.relations}
				shapes={props.draft.shapes}
				onAdd={props.actions.addShape}
				onRemove={props.actions.removeShape}
			/>
			{props.usage && (
				<OrphanInbox
					slice={props.draft}
					usage={props.usage}
					onAbsorbAll={props.onAbsorbAll}
					onAbsorbOne={props.onAbsorbOrphan}
				/>
			)}
		</div>
	)
}
